import { useState } from 'react';
import PropTypes from 'prop-types';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { KeenIcon } from '@/components';
import { endOfDay, subYears } from 'date-fns';
import {
  parseTypedDate,
  reanchorToMonth,
  isDateAllowed,
  toIsoDate,
  stripTime,
} from '@/components/form/datePickerValue';

const DatePicker = ({
  field,
  form,
  isRange = false,
  transparent = false,
  disabled = false,
  defaultPlaceholder = 'Pick a date',
  clearDateTitle = 'Clear Date',
  disableFuture = false,
  disablePast = false,
  ...props
}) => {
  const { setFieldValue } = form;
  const { value } = field;

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  // Controlled so the month/year dropdowns keep their position while open.
  const [month, setMonth] = useState(null);

  const isDob = props.isDob ?? false;

  // ---------- HELPERS ----------
  const formatToString = toIsoDate;

  const parseStored = (val) => {
    if (!val) return null;
    return new Date(val + "T00:00:00"); // ensure correct date without shift
  };

  const normalizeIncomingDate = (val) => {
    if (!val) return null;

    // Already in yyyy-mm-dd
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
      return val;
    }

    // Replace / with -
    let clean = val.replace(/\//g, "-").trim();

    // Possible cases:
    // yyyy-mm-dd
    // dd-mm-yyyy
    // mm-dd-yyyy
    // yyyy-mm-dd
    // Month dd, yyyy
    // dd Mon yyyy

    // Case - textual month (Oct, March, etc)
    const textParsed = new Date(clean);
    if (!isNaN(textParsed)) {
      return format(textParsed, "yyyy-MM-dd");
    }

    // Split into 3 parts
    const parts = clean.split("-");
    if (parts.length !== 3) return null;

    let [a, b, c] = parts.map((p) => parseInt(p));

    // yyyy-mm-dd > valid
    if (a > 1900 && a < 2100) {
      return format(new Date(a, b - 1, c), "yyyy-MM-dd");
    }

    // Now a & b could be day/month or month/day. Decide

    // If a > 12 => definitely DD-MM-YYYY
    if (a > 12) {
      return format(new Date(c, b - 1, a), "yyyy-MM-dd");
    }

    // If b > 12 => definitely MM-DD-YYYY
    if (b > 12) {
      return format(new Date(c, a - 1, b), "yyyy-MM-dd");
    }

    // If both <= 12 → ambiguous → assume MM-DD-YYYY (common US format)
    return format(new Date(c, a - 1, b), "yyyy-MM-dd");
  };


  // const parsedValue = !isRange ? parseStored(value) : value;
  const normalized = !isRange && value ? normalizeIncomingDate(value) : value;
  const parsedValue = normalized ? new Date(normalized + "T00:00:00") : null;

  const maxDate = subYears(new Date(), 18);
  const currentYear = new Date().getFullYear();

  const defaultMonth = parsedValue ? parsedValue : (isDob ? maxDate : new Date());
  const toYearLimit = isDob ? currentYear : currentYear + 50;

  const today = stripTime(new Date());

  // ---------- TYPED INPUT ----------

  const dobLimit = () => endOfDay(subYears(new Date(), 18));

  const allowed = (date) => {
    if (!isDateAllowed(date, { disableFuture, disablePast, today })) return false;

    return !(isDob && date > dobLimit());
  };

  // Commit whatever is in the type-in box. Called on Enter and on blur, and
  // again when the popover closes, so a typed date is never silently dropped
  // just because the user did not press Enter (OL-90).
  const commitTypedInput = () => {
    const parsed = parseTypedDate(search);
    if (!parsed || !allowed(parsed)) return false;

    const formatted = formatToString(parsed);
    setSearch('');

    if (!isRange) {
      setFieldValue(field.name, formatted);
      return true;
    }

    if (!value?.from || (value?.from && value?.to)) {
      setFieldValue(field.name, { from: formatted, to: null });
      return false;
    }

    setFieldValue(field.name, { from: value.from, to: formatted });
    return true;
  };

  const handleSearchEnter = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();

    if (commitTypedInput()) setOpen(false);
  };

  // ---------- MONTH / YEAR NAVIGATION ----------

  // Moving an already-chosen date onto the month or year the user navigated to
  // is what they mean by changing the year dropdown. With nothing chosen yet
  // there is nothing to move, so this only scrolls the calendar (OL-90).
  const handleMonthChange = (nextMonth) => {
    setMonth(nextMonth);

    if (isRange || !normalized) return;

    const moved = reanchorToMonth(normalized, nextMonth);
    if (!moved) return;

    const movedDate = new Date(`${moved}T00:00:00`);
    if (!allowed(movedDate)) return;

    setFieldValue(field.name, moved);
  };

  // ---------- CALENDAR SELECT ----------
  const handleDateSelect = (date) => {
    if (disabled) return;
    if (!date || isNaN(date)) return;
    if (!isRange && disableFuture && stripTime(date) > today) return;
    if (!isRange && disablePast && stripTime(date) < today) return;

    const formatted = formatToString(date);

    if (!isRange) {
      setFieldValue(field.name, formatted);
      setOpen(false);
      return;
    }

    if (Array.isArray(date) && date[0] && date[1]) {
      setFieldValue(field.name, {
        from: formatToString(date[0]),
        to: formatToString(date[1])
      });
      setOpen(false);
    }
  };

  // ---------- CLEAR ----------
  const handleClearDate = () => {
    setSearch('');
    setFieldValue(field.name, isRange ? { from: null, to: null } : null);
    setOpen(false);
  };

  const handleSelectToday = () => {
    const currentDate = today;
    if (disableFuture && currentDate > today) return;
    if (isDob) {
      const dobMax = endOfDay(subYears(new Date(), 18));
      if (currentDate > dobMax) return;
    }
    if (disablePast && currentDate < today) return;
    const formatted = formatToString(currentDate);
    setSearch('');
    if (isRange) {
      setFieldValue(field.name, { from: formatted, to: formatted });
    } else {
      setFieldValue(field.name, formatted);
    }
    setOpen(false);
  };

  const handleOpenChange = (next) => {
    if (next) {
      setMonth(defaultMonth);
      setSearch('');
    } else {
      commitTypedInput();
    }

    setOpen(next);
  };

  // ---------- DISPLAY ----------
  const formatDateDisplay = () => {
    if (isRange && value?.from && value?.to) {
      return `${format(parseStored(value.from), 'MMM dd, yyyy')} - ${format(parseStored(value.to), 'MMM dd, yyyy')}`;
    }
    return parsedValue ? format(parsedValue, 'MMM dd, yyyy') : defaultPlaceholder;
  };

  const todayAllowed = (() => {
    const currentDate = today;
    if (disableFuture && currentDate > today) return false;
    if (isDob) {
      const dobMax = endOfDay(subYears(new Date(), 18));
      if (currentDate > dobMax) return false;
    }
    return true;
  })();

  const disabledDates = (() => {
    if (isDob) {
      return { after: endOfDay(subYears(new Date(), 18)) };
    }

    const calendarDisabledDates = {};

    if (disableFuture) {
      calendarDisabledDates.after = today;
    }

    if (disablePast) {
      calendarDisabledDates.before = today;
    }

    return Object.keys(calendarDisabledDates).length ? calendarDisabledDates : undefined;
  })();

  return (
    <Popover open={!disabled && open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={`${transparent ? 'transparent-input' : ''} btn btn-sm btn-light w-full h-[40px]`}
        >
          <KeenIcon icon="calendar" className="me-0.5" />
          {formatDateDisplay()}
        </button>
      </PopoverTrigger>

      {!disabled && (
        <PopoverContent className="w-auto p-0" align="end">

          <div className="p-3 pb-0">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleSearchEnter}
              onBlur={commitTypedInput}
              placeholder="Type a date, e.g. 03/15/2028"
              className="input input-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              Type a date, or pick a day below. Changing the month or year moves
              an already-chosen date.
            </p>
          </div>

          <Calendar
            selected={
              isRange
                ? {
                  from: value?.from ? parseStored(value.from) : null,
                  to: value?.to ? parseStored(value.to) : null
                }
                : parsedValue
            }
            onSelect={handleDateSelect}
            mode={isRange ? 'range' : 'single'}
            month={month ?? defaultMonth}
            onMonthChange={handleMonthChange}
            captionLayout="dropdown"
            fromYear={isDob ? 1900 : 1950}
            toYear={toYearLimit}
            disabled={disabledDates}
            classNames={{
              caption_dropdowns: "flex gap-2 mt-1 justify-center",
              dropdown:
                "h-8 max-w-[130px] truncate rounded-md border border-input bg-background px-2 py-1 text-sm shadow-sm focus:outline-none focus:ring-0 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none",
            }}
          />
    

          <div className="flex items-center gap-2 mx-3 mb-3">
            <button
              type="button"
              onClick={handleSelectToday}
              disabled={!todayAllowed}
              className="btn btn-xs btn-outline btn-primary"
            >
              Today
            </button>
            <button
              type="button"
              onClick={handleClearDate}
              className="btn btn-xs btn-outline btn-danger"
            >
              {clearDateTitle}
            </button>
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
};

DatePicker.propTypes = {
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        from: PropTypes.string,
        to: PropTypes.string
      })
    ])
  }).isRequired,
  form: PropTypes.shape({
    setFieldValue: PropTypes.func.isRequired
  }).isRequired,
  isRange: PropTypes.bool,
  transparent: PropTypes.bool,
  disabled: PropTypes.bool,
  defaultPlaceholder: PropTypes.string,
  clearDateTitle: PropTypes.string,
  disableFuture: PropTypes.bool,
  disablePast: PropTypes.bool,
  isDob: PropTypes.bool
};

export { DatePicker };

